// @ai-generated 2026-08-28T07:59:23+00:00 by matthias.gessinger@synqony.com using Claude Opus 5 (1M context)
import { Badge } from '@synqo/frontend-component-library';
import { IconChevronRight } from '@tabler/icons-react';
import {
    type ExportRunFinding,
    type ExportRunFindingGroup,
    exportRunFindingGroups,
    type RunResult,
} from '@utils/exportRunResult';
import { useState } from 'react';
import { MessageLevelIcon } from '../Messages/MessageLevelIcon';
import { getVariantByMessageLevel } from '../Messages/messageVariant';
import styles from './ExportRunFindings.module.css';

/** More lines than this under one heading is a list to scan, not a message to read. */
const FOLD_GROUP_ABOVE_FINDINGS = 3;

/** A message longer than roughly one line of the panel is shown as its first line until asked. */
const FOLD_MESSAGE_ABOVE_CHARACTERS = 100;

interface ExportRunFindingsProps {
    run: RunResult;
}

/**
 * What the run reported, grouped the way it was produced. A schema check does not report one
 * problem, it reports forty variations of the same one, so the heading and the wording they share
 * are stated once and each line carries only what distinguishes it — the position in the script it
 * complains about above all.
 *
 * Everything folds, on two levels, because a message is long for two independent reasons. A heading
 * folds away many messages at once; a single message folds down to one line, so that the next one is
 * always in view rather than a screen below the end of its neighbour. Both are decided from the
 * data, so a run with three short remarks is simply three short remarks.
 */
export function ExportRunFindings({ run }: ExportRunFindingsProps) {
    const groups = exportRunFindingGroups(run);

    return (
        <div className={styles.groups}>
            {groups.map((group) => (
                <FindingGroup key={group.title} group={group} />
            ))}
        </div>
    );
}

function FindingGroup({ group }: { group: ExportRunFindingGroup }) {
    return (
        <details className={styles.group} open={!startsFolded(group)}>
            <summary className={styles.groupHeader}>
                <IconChevronRight className={styles.chevron} size={14} />
                <MessageLevelIcon variant={getVariantByMessageLevel(group.level)} size="sm" />
                <span className={styles.groupTitle}>{group.title}</span>
                {group.sharedPrefix && <span className={styles.groupPrefix}>{group.sharedPrefix}</span>}
                {group.count > 1 && (
                    <Badge size="xxs" type="counter" variant="default">
                        {String(group.count)}
                    </Badge>
                )}
            </summary>
            <ul className={styles.list}>
                {group.findings.map((finding, position) => (
                    <Finding key={`${finding.text}#${finding.location ?? position}`} finding={finding} />
                ))}
            </ul>
        </details>
    );
}

/**
 * One reported problem. A schema message runs to several lines of prose, and a list of those is a
 * list in which only the first entry can be found, so a long message is cut to its first line and
 * opens on request. A message that fits stays a plain line: there is nothing to reveal, and a
 * control that reveals nothing is worse than none.
 */
function Finding({ finding }: { finding: ExportRunFinding }) {
    const [expanded, setExpanded] = useState(false);
    const foldable = finding.text.length > FOLD_MESSAGE_ABOVE_CHARACTERS;

    return (
        <li
            className={styles.finding}
            data-variant={getVariantByMessageLevel(finding.level)}
            data-expanded={foldable ? expanded : undefined}
        >
            {foldable ? (
                <button
                    type="button"
                    className={styles.findingToggle}
                    aria-expanded={expanded}
                    aria-label={expanded ? 'Meldung einklappen' : 'Ganze Meldung anzeigen'}
                    onClick={() => setExpanded((open) => !open)}
                >
                    <IconChevronRight className={styles.chevron} size={12} />
                    <span className={styles.findingText}>{finding.text}</span>
                </button>
            ) : (
                <span className={styles.findingText}>{finding.text}</span>
            )}
            {finding.location && <code className={styles.location}>{finding.location}</code>}
            {finding.occurrences > 1 && <Badge size="xxs" variant="default">{`${finding.occurrences}×`}</Badge>}
        </li>
    );
}

/**
 * Whether a group is worth hiding behind its heading. Only the number of messages decides it — their
 * length is the single message's business now — and a group the backend gave no heading is never
 * folded, since its summary line would be the word "Meldung" and nothing else.
 */
function startsFolded(group: ExportRunFindingGroup): boolean {
    return group.titled && group.findings.length > FOLD_GROUP_ABOVE_FINDINGS;
}
